import { getPresetShortcutTitles } from "@/data/preset-shortcut-titles";
import { globals } from "@/globals";
import {
	BookmarkInfo,
	FolderInfo,
	GeneralSettingsInfo,
	SearchEngineInfo,
} from "@/types";

const CUSTOM_SEARCH_ENGINES_KEY = "customSearchEngines";
const DOCK_KEY = "dockItems";
const GENERAL_SETTINGS_KEY = "generalSettings";
const HIDDEN_PRESET_SHORTCUTS_KEY = "hiddenPresetShortcuts";
const LAST_SEARCH_ENGINE_ID_KEY = "lastSearchEngineId";
const SEARCH_HISTORY_KEY = "searchHistory";
const SELECTED_SEARCH_ENGINE_ID_KEY = "selectedSearchEngineId";
const SHORTCUTS_KEY = "shortcuts";
const VERSION_KEY = "localVersion";

class LocalStorage {
	public static deletePrivateData(): void {
		this.deleteShortcuts();
		this.deleteDockItems();
		this.deleteSearchEngines();
	}

	public static deleteDockItems(): void {
		localStorage.removeItem(DOCK_KEY);
	}

	public static deleteGeneralSettings(): void {
		localStorage.removeItem(GENERAL_SETTINGS_KEY);
	}

	public static deleteSearchEngines(): void {
		localStorage.removeItem(CUSTOM_SEARCH_ENGINES_KEY);
	}

	public static deleteSearchHistory(): void {
		localStorage.removeItem(SEARCH_HISTORY_KEY);
	}

	public static deleteShortcuts(): void {
		localStorage.removeItem(SHORTCUTS_KEY);
	}

	public static getCustomSearchEngines(): SearchEngineInfo[] {
		try {
			const searchEngines = localStorage.getItem(
				CUSTOM_SEARCH_ENGINES_KEY,
			);
			if (!searchEngines) {
				return [];
			}
			return JSON.parse(searchEngines) as SearchEngineInfo[];
		} catch (error) {
			console.error(error);
			return [];
		}
	}

	public static getDockItems(): BookmarkInfo[] | null {
		try {
			const dockItems = localStorage.getItem(DOCK_KEY);
			if (!dockItems) {
				return null;
			}
			const results = JSON.parse(dockItems) as BookmarkInfo[];
			if (!Array.isArray(results)) {
				return null;
			}
			return results;
		} catch {
			return null;
		}
	}

	public static getGeneralSettings(): GeneralSettingsInfo {
		this.#upgradeLocalVersion(); // clears old storage before settings being read
		try {
			const generalSettings = localStorage.getItem(GENERAL_SETTINGS_KEY);
			if (!generalSettings) {
				return {};
			}
			return JSON.parse(generalSettings) as GeneralSettingsInfo;
		} catch (error) {
			console.error(error);
			this.deleteGeneralSettings();
			window.alert(error);
			return {};
		}
	}

	public static getLastSearchEngineId(): string | null {
		return localStorage.getItem(LAST_SEARCH_ENGINE_ID_KEY);
	}

	public static getPresetShortcutTitles(): BookmarkInfo[] {
		const titles = getPresetShortcutTitles();
		const hiddenPresetShortcuts = localStorage.getItem(
			HIDDEN_PRESET_SHORTCUTS_KEY,
		);
		if (!hiddenPresetShortcuts) {
			return titles;
		}
		const hiddenSet = new Set(
			JSON.parse(hiddenPresetShortcuts) as string[],
		);
		return titles.filter((title) => !hiddenSet.has(title.title));
	}

	public static getSearchHistory(): string[] {
		try {
			const searchHistory = localStorage.getItem(SEARCH_HISTORY_KEY);
			if (!searchHistory) {
				return [];
			}
			return JSON.parse(searchHistory) as string[];
		} catch (error) {
			console.error(error);
			return [];
		}
	}

	public static getSelectedSearchEngineId(): string | null {
		return localStorage.getItem(SELECTED_SEARCH_ENGINE_ID_KEY);
	}

	public static getShortcuts(): (BookmarkInfo | FolderInfo)[] | null {
		try {
			const shortcuts = localStorage.getItem(SHORTCUTS_KEY);
			if (!shortcuts) {
				return null;
			}
			const results = JSON.parse(shortcuts) as BookmarkInfo[];
			if (!Array.isArray(results)) {
				return null;
			}
			return results;
		} catch {
			return null;
		}
	}

	public static hidePresetShortcut(title: string): void {
		const hiddenPresetShortcuts = localStorage.getItem(
			HIDDEN_PRESET_SHORTCUTS_KEY,
		);
		const hiddenSet = new Set(
			hiddenPresetShortcuts
				? (JSON.parse(hiddenPresetShortcuts) as string[])
				: [],
		);
		hiddenSet.add(title);
		localStorage.setItem(
			HIDDEN_PRESET_SHORTCUTS_KEY,
			JSON.stringify(Array.from(hiddenSet)),
		);
	}

	public static resetSelectedSearchEngine(): void {
		localStorage.removeItem(LAST_SEARCH_ENGINE_ID_KEY);
		localStorage.removeItem(SELECTED_SEARCH_ENGINE_ID_KEY);
	}

	public static saveDockItems(dockItems: BookmarkInfo[]): void {
		localStorage.setItem(DOCK_KEY, JSON.stringify(dockItems));
	}

	public static saveLastSearchEngineId(id: string): void {
		localStorage.setItem(LAST_SEARCH_ENGINE_ID_KEY, id);
	}

	public static saveSearchEngines(searchEngines: SearchEngineInfo[]): void {
		if (searchEngines.length === 0) {
			this.deleteSearchEngines();
			return;
		}
		localStorage.setItem(
			CUSTOM_SEARCH_ENGINES_KEY,
			JSON.stringify(searchEngines),
		);
	}

	public static saveSearchHistory(searchHistory: string[]): void {
		localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(searchHistory));
	}

	public static saveSelectedSearchEngineId(id: string): void {
		localStorage.setItem(SELECTED_SEARCH_ENGINE_ID_KEY, id);
	}

	public static saveShortcuts(
		shortcuts: (BookmarkInfo | FolderInfo)[],
	): void {
		localStorage.setItem(SHORTCUTS_KEY, JSON.stringify(shortcuts));
	}

	public static unhideAllPresetShortcuts(): void {
		localStorage.removeItem(HIDDEN_PRESET_SHORTCUTS_KEY);
	}

	public static updateGeneralSettings(newValues: GeneralSettingsInfo): void {
		const currentSettings = this.getGeneralSettings();
		const newSettings = {
			...currentSettings,
			...newValues,
		};
		if (Object.keys(newSettings).length === 0) {
			this.deleteGeneralSettings();
			return;
		}
		localStorage.setItem(GENERAL_SETTINGS_KEY, JSON.stringify(newSettings));
	}

	static #upgradeLocalVersion(): void {
		const version = localStorage.getItem(VERSION_KEY);
		if (version) {
			const isUpgradedFromV2 =
				version.includes("w") && version.includes("a");
			if (isUpgradedFromV2) {
				globals.isMajorUpdate = true;
				localStorage.clear(); // avoid unexpected conflicts in the future
			} else {
				const currentVersionParts = globals.VERSION.split(".");
				const lastVersionParts = version.split(".");
				globals.isMajorUpdate =
					currentVersionParts[0] > lastVersionParts[0] ||
					(currentVersionParts[0] === lastVersionParts[0] &&
						currentVersionParts[1] > lastVersionParts[1]);
			}
		} else {
			globals.isFirstRun = true;
		}

		// Update the version in the local storage
		localStorage.setItem(VERSION_KEY, globals.VERSION);
	}
}

export default LocalStorage;
